import React from 'react'
import {
    getValidLinkedSourceTypes,
    getValidSourceTypes,
    getValidUploadedSourceTypes,
} from '../../../../utils/issues/sourceTypes'
import Typography from '@mui/material/Typography'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select, { SelectProps } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormHelperText from '@mui/material/FormHelperText'
import { Controller, RegisterOptions, UseFormReturn } from 'react-hook-form'
import { IssueType } from 'types/IssueEntity'
import { SourceType } from 'types/SourceType'
import { intersection } from './utils'
import { SourceForm } from './AdminSourceFormRow'

const sourceTypes = {
    linkedSource: getValidLinkedSourceTypes(),
    uploadedSource: getValidUploadedSourceTypes(),
}

export function SourceTypeSelect({
    label,
    name,
    formMethods,
    validation,
    props,
    setOfTypes = 'linkedSource',
    issueType,
    excludeSourceTypes,
}: {
    label: string
    name: keyof SourceForm
    formMethods: UseFormReturn<SourceForm>
    validation?: RegisterOptions<SourceForm>
    props?: SelectProps
    setOfTypes?: 'linkedSource' | 'uploadedSource'
    issueType: IssueType | 'warrant' | 'convertible' | null
    excludeSourceTypes: SourceType[]
}) {
    let typesToList = issueType
        ? intersection(sourceTypes[setOfTypes], getValidSourceTypes(issueType))
        : sourceTypes[setOfTypes]

    if (excludeSourceTypes) {
        typesToList = typesToList.filter((type) => !excludeSourceTypes.includes(type))
    }

    if (!['linkedSource', 'uploadedSource'].includes(setOfTypes)) {
        console.error('Invalid setOfTypes: ' + setOfTypes)
        return null
    }

    const error = formMethods.formState.errors[name]
    if (typesToList.length === 0) {
        return <Typography>Inga tillåtna källtyper</Typography>
    }
    if (typesToList.length === 1) {
        if (formMethods.getValues()[name] !== typesToList[0]) {
            formMethods.setValue(name, typesToList[0])
        }

        return <Typography>Källtyp: {typesToList[0]}</Typography>
    }

    return (
        <FormControl sx={{ minWidth: 150 }} error={!!error}>
            <InputLabel id={`${name}-source-type-select-label`}>{label}</InputLabel>
            <Controller
                name={name}
                control={formMethods.control}
                rules={validation}
                defaultValue={typesToList[0]}
                render={({ field }) => (
                    <Select
                        defaultValue={typesToList.length === 1 ? typesToList[0] : undefined}
                        labelId={`${name}-source-type-select-label`}
                        label={label}
                        sx={{ minWidth: 150 }}
                        {...field}
                        {...props}
                    >
                        <MenuItem value='' disabled>
                            Välj källtyp
                        </MenuItem>
                        {typesToList.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            />
            {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
    )
}
